import React from 'react';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';
import { NEWTON_LOGO_LIGHT_SRC, NEWTON_LOGO_SRC } from '@/utils/nShowcase';

import { asset } from '../../../lib/asset';
import { LightLockup, Lockup, Mark, NewtonLockup, Wordmark } from './Logo.styles';

export type LogoProps = {
  /** Force the light (dark-on-light) lockup for a light surface inside the dark
   *  theme (e.g. the in-app preview overlay). The light theme uses it anyway. */
  dark?: boolean;
};

/**
 * Whitelabels.com lockup: masked brand mark + wordmark, lifted 1:1 from Figma.
 *
 * Theme-aware: the white lockup renders on the dark theme; on the light theme
 * (or when `dark` forces a light surface) a purpose-built dark-on-light SVG
 * renders instead, so the brand never vanishes on a light header. The CSS
 * (`Lockup` / `LightLockup`) toggles them off `data-ows-theme`, so no re-render.
 *
 * While the Newton showcase is on (the owner's `n_showcase` preference or the
 * local switch, see `utils/nShowcase`) Newton's logo shows instead, with the
 * same dark / light split.
 */
export const Logo = ({ dark = false }: LogoProps): React.ReactElement => {
  const newtonShowcase = useAppSelector(selectNShowcase);
  const mask = `url("${asset('logo-mark-mask.svg')}")`;
  if (newtonShowcase) {
    return (
      <>
        <NewtonLockup $dark={dark} src={NEWTON_LOGO_SRC} alt="Newton" />
        <LightLockup $dark={dark} src={NEWTON_LOGO_LIGHT_SRC} alt="Newton" />
      </>
    );
  }
  return (
    <>
      <Lockup $dark={dark}>
        <Mark $mask={mask}>
          <img src={asset('logo-mark.svg')} alt="" />
        </Mark>
        <Wordmark src={asset('logo-wordmark.svg')} alt="Whitelabels.com" />
      </Lockup>
      <LightLockup $dark={dark} src={asset('logo-light.svg')} alt="Whitelabels.com" />
    </>
  );
};

export default Logo;
